import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import FullScreenLoader from '../components/FullScreenLoader';

const DEFAULT_LABEL = 'Loading...';

interface LoaderContextType {
  show: (label?: string) => void;
  hide: () => void;
  isVisible: boolean;
  label: string;
  withLoader: <T>(label: string, fn: () => Promise<T>) => Promise<T>;
}

const LoaderContext = createContext<LoaderContextType | undefined>(undefined);

export const LoaderProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [count, setCount] = useState(0);
  const [label, setLabel] = useState<string>(DEFAULT_LABEL);
  const labelStackRef = useRef<string[]>([]);

  const show = useCallback((nextLabel?: string) => {
    const resolved = nextLabel ?? DEFAULT_LABEL;
    labelStackRef.current.push(resolved);
    setLabel(resolved);
    setCount((c) => c + 1);
  }, []);

  const hide = useCallback(() => {
    labelStackRef.current.pop();
    const top = labelStackRef.current[labelStackRef.current.length - 1] ?? DEFAULT_LABEL;
    setLabel(top);
    setCount((c) => Math.max(0, c - 1));
  }, []);

  const withLoader = useCallback(
    async <T,>(loaderLabel: string, fn: () => Promise<T>): Promise<T> => {
      show(loaderLabel);
      try {
        return await fn();
      } finally {
        hide();
      }
    },
    [show, hide],
  );

  const value = useMemo<LoaderContextType>(
    () => ({ show, hide, isVisible: count > 0, label, withLoader }),
    [show, hide, count, label, withLoader],
  );

  return (
    <LoaderContext.Provider value={value}>
      {children}
      <FullScreenLoader visible={count > 0} label={label} />
    </LoaderContext.Provider>
  );
};

export const useLoader = (): LoaderContextType => {
  const ctx = useContext(LoaderContext);
  if (!ctx) {
    throw new Error('useLoader must be used inside a <LoaderProvider>');
  }
  return ctx;
};
